import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc, collection, addDoc } from 'firebase/firestore';
import { db } from '../config/firebase';
import { Auction, AuctionLot } from '../types/auction.types';
import { useAuth } from '../contexts/AuthContext';
import { useWallet } from '../contexts/WalletContext';
import { format } from 'date-fns';
import toast from 'react-hot-toast';

const AuctionDetail: React.FC = () => {
  const { auctionId } = useParams<{ auctionId: string }>();
  const navigate = useNavigate();
  const { currentUser, userProfile } = useAuth();
  const { balance, updateBalance } = useWallet();
  const [auction, setAuction] = useState<Auction | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedLot, setSelectedLot] = useState<AuctionLot | null>(null);
  const [predictionAmount, setPredictionAmount] = useState('');
  const [wagerAmount, setWagerAmount] = useState('');
  const [executeAsBid, setExecuteAsBid] = useState(false);
  const [maxBidAmount, setMaxBidAmount] = useState('');
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);

  useEffect(() => {
    loadAuction();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auctionId]);

  const loadAuction = async () => {
    if (!auctionId) return;

    try {
      const auctionDoc = await getDoc(doc(db, 'auctions', auctionId));
      
      if (auctionDoc.exists()) {
        const data = auctionDoc.data();
        setAuction({
          id: auctionDoc.id,
          ...data,
          startDate: data.startDate?.toDate() || new Date(),
          endDate: data.endDate?.toDate() || new Date(),
          createdAt: data.createdAt?.toDate() || new Date(),
          updatedAt: data.updatedAt?.toDate() || new Date(),
        } as Auction);
      } else {
        toast.error('Auction not found');
        navigate('/auctions');
      }
    } catch (error) {
      console.error('Error loading auction:', error);
      toast.error('Failed to load auction');
    } finally {
      setLoading(false);
    }
  };

  const handlePrediction = (lot: AuctionLot) => {
    if (!currentUser) {
      toast.error('Please log in to make predictions');
      navigate('/login');
      return;
    }
    setSelectedLot(lot);
    setPredictionAmount('');
    setWagerAmount('');
    setExecuteAsBid(false);
    setMaxBidAmount('');
    setSelectedImageIndex(0);
  };

  const submitPrediction = async () => {
    if (!predictionAmount || !selectedLot || !auction || !wagerAmount) {
      toast.error('Please enter prediction and wager amount');
      return;
    }

    if (!currentUser || !userProfile) {
      toast.error('Please log in to make predictions');
      return;
    }

    const wager = parseFloat(wagerAmount);
    if (wager > balance) {
      toast.error('Insufficient Ringer$ balance');
      return;
    }

    if (wager < 10) {
      toast.error('Minimum wager is 10 Ringer$');
      return;
    }

    // Validate max bid if executeAsBid is checked
    if (executeAsBid) {
      const maxBid = parseFloat(maxBidAmount);
      if (!maxBidAmount || maxBid <= 0) {
        toast.error('Please enter your maximum bid amount');
        return;
      }
      if (maxBid < selectedLot.startingBid) {
        toast.error(`Maximum bid must be at least the starting bid of $${selectedLot.startingBid}`);
        return;
      }
    }

    try {
      const predictedOutsideRange = selectedLot.expertLowEstimate && selectedLot.expertHighEstimate
        ? (parseFloat(predictionAmount) < selectedLot.expertLowEstimate || 
           parseFloat(predictionAmount) > selectedLot.expertHighEstimate)
        : false;

      const prediction = {
        userId: currentUser.uid,
        userEmail: currentUser.email || '',
        userName: userProfile.displayName || currentUser.email || 'Anonymous',
        auctionId: auction.id,
        auctionTitle: auction.title,
        lotId: selectedLot.id,
        lotSku: selectedLot.sku,
        lotTitle: selectedLot.title,
        lotImage: selectedLot.images && selectedLot.images.length > 0 ? selectedLot.images[0] : null,
        predictedPrice: parseFloat(predictionAmount),
        wagerAmount: wager,
        expertLowEstimate: selectedLot.expertLowEstimate || null,
        expertHighEstimate: selectedLot.expertHighEstimate || null,
        predictedOutsideRange,
        // Left Bid fields
        executeAsBid: executeAsBid,
        maxBidAmount: executeAsBid ? parseFloat(maxBidAmount) : null,
        bidStatus: executeAsBid ? 'pending' : null,
        bidExecutedAt: null,
        bidExecutedBy: null,
        bidNotes: null,
        // Standard fields
        actualPrice: null,
        startingBid: selectedLot.startingBid,
        status: 'pending',
        ringersEarned: null,
        multiplier: null,
        outcomeType: null,
        createdAt: new Date(),
        settledAt: null
      };

      await addDoc(collection(db, 'predictions'), prediction);
      
      // Deduct wager from balance
      await updateBalance(-wager);
      
      const message = executeAsBid 
        ? `Prediction placed and left bid submitted! Max bid: $${maxBidAmount}`
        : `Prediction placed! Wagered ${wager} R$ on ${selectedLot.title}`;
      
      toast.success(message);
      setSelectedLot(null);
      setPredictionAmount('');
      setWagerAmount('');
      setMaxBidAmount('');
      setExecuteAsBid(false);
      setSelectedImageIndex(0);
    } catch (error) {
      console.error('Error submitting prediction:', error);
      toast.error('Failed to submit prediction');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-xl">Loading auction...</div>
      </div>
    );
  }

  if (!auction) {
    return (
      <div className="card text-center py-12">
        <p className="text-xl text-gray-600">Auction not found</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Auction Header */}
      <div className="card">
        <button
          onClick={() => navigate('/auctions')}
          className="text-primary-600 hover:text-primary-700 mb-4"
        >
          ← Back to Auctions
        </button>
        
        <h1 className="text-3xl font-bold mb-2">{auction.title}</h1>
        <p className="text-gray-600 mb-4">{auction.auctionHouseName}</p>
        
        {auction.description && (
          <p className="text-gray-700 mb-4">{auction.description}</p>
        )}

        <div className="grid md:grid-cols-3 gap-4 mt-6">
          <div className="bg-gray-50 p-4 rounded-lg">
            <p className="text-sm text-gray-600 mb-1">Start Date</p>
            <p className="font-semibold">{format(new Date(auction.startDate), 'MMM dd, yyyy h:mm a')}</p>
          </div>
          <div className="bg-gray-50 p-4 rounded-lg">
            <p className="text-sm text-gray-600 mb-1">End Date</p>
            <p className="font-semibold">{format(new Date(auction.endDate), 'MMM dd, yyyy h:mm a')}</p>
          </div>
          <div className="bg-gray-50 p-4 rounded-lg">
            <p className="text-sm text-gray-600 mb-1">Total Lots</p>
            <p className="font-semibold">{auction.totalLots}</p>
          </div>
        </div>
      </div>

      {/* Lots Grid */}
      <div className="card">
        <h2 className="text-2xl font-bold mb-4">Auction Lots</h2>
        
        {auction.lots && auction.lots.length > 0 ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {auction.lots.map((lot) => (
              <div key={lot.id} className="border rounded-lg overflow-hidden hover:shadow-lg transition-shadow">
                {/* Image */}
                <div className="relative bg-gray-100 h-48">
                  {lot.images && lot.images.length > 0 ? (
                    <img
                      src={lot.images[0]}
                      alt={lot.title}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.currentTarget.src = 'https://via.placeholder.com/400x300?text=No+Image';
                      }}
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-gray-400">
                      <span className="text-4xl">📦</span>
                    </div>
                  )}
                  {lot.images && lot.images.length > 1 && (
                    <div className="absolute bottom-2 right-2 bg-black bg-opacity-60 text-white px-2 py-1 rounded text-xs">
                      +{lot.images.length - 1} more
                    </div>
                  )}
                </div>

                {/* Content */}
                <div className="p-4">
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex-1">
                      <p className="text-xs text-gray-600 mb-1">SKU: {lot.sku}</p>
                      <h3 className="font-bold line-clamp-2">{lot.title}</h3>
                    </div>
                    <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs whitespace-nowrap ml-2">
                      {lot.category}
                    </span>
                  </div>

                  <p className="text-sm text-gray-600 mb-3 line-clamp-2">
                    {lot.description}
                  </p>

                  <div className="space-y-1 text-sm mb-4">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Starting Bid:</span>
                      <span className="font-semibold">${lot.startingBid}</span>
                    </div>
                    {lot.expertLowEstimate && lot.expertHighEstimate && (
                      <div className="flex justify-between">
                        <span className="text-gray-600">Expert Range:</span>
                        <span className="font-semibold text-blue-600">
                          ${lot.expertLowEstimate} - ${lot.expertHighEstimate}
                        </span>
                      </div>
                    )}
                    {lot.estimatedValue && (
                      <div className="flex justify-between">
                        <span className="text-gray-600">Est. Value:</span>
                        <span className="font-semibold">{lot.estimatedValue}</span>
                      </div>
                    )}
                    {lot.condition && (
                      <div className="flex justify-between">
                        <span className="text-gray-600">Condition:</span>
                        <span className="font-semibold">{lot.condition}</span>
                      </div>
                    )}
                  </div>

                  <button
                    onClick={() => handlePrediction(lot)}
                    className="w-full btn-primary text-sm"
                  >
                    Make Prediction
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-center text-gray-600 py-8">No lots available</p>
        )}
      </div>

      {/* Prediction Modal */}
      {selectedLot && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <h3 className="text-xl font-bold mb-4">Make Your Prediction</h3>
              
              {/* Image Gallery */}
              {selectedLot.images && selectedLot.images.length > 0 && (
                <div className="mb-4">
                  {/* Main Image */}
                  <div className="relative bg-gray-100 rounded-lg overflow-hidden mb-2" style={{ height: '300px' }}>
                    <img
                      src={selectedLot.images[selectedImageIndex]}
                      alt={`${selectedLot.title} - Image ${selectedImageIndex + 1}`}
                      className="w-full h-full object-contain"
                      onError={(e) => {
                        e.currentTarget.src = 'https://via.placeholder.com/600x400?text=Image+Not+Found';
                      }}
                    />
                    
                    {/* Navigation Arrows */}
                    {selectedLot.images.length > 1 && (
                      <>
                        <button
                          onClick={() => setSelectedImageIndex(prev => 
                            prev === 0 ? selectedLot.images.length - 1 : prev - 1
                          )}
                          className="absolute left-2 top-1/2 -translate-y-1/2 bg-black bg-opacity-50 text-white p-2 rounded-full hover:bg-opacity-70"
                        >
                          ←
                        </button>
                        <button
                          onClick={() => setSelectedImageIndex(prev => 
                            prev === selectedLot.images.length - 1 ? 0 : prev + 1
                          )}
                          className="absolute right-2 top-1/2 -translate-y-1/2 bg-black bg-opacity-50 text-white p-2 rounded-full hover:bg-opacity-70"
                        >
                          →
                        </button>
                        <div className="absolute bottom-2 right-2 bg-black bg-opacity-60 text-white px-2 py-1 rounded text-xs">
                          {selectedImageIndex + 1} / {selectedLot.images.length}
                        </div>
                      </>
                    )}
                  </div>

                  {/* Thumbnail Strip */}
                  {selectedLot.images.length > 1 && (
                    <div className="flex gap-2 overflow-x-auto pb-2">
                      {selectedLot.images.map((image, idx) => (
                        <button
                          key={idx}
                          onClick={() => setSelectedImageIndex(idx)}
                          className={`flex-shrink-0 w-16 h-16 rounded border-2 overflow-hidden ${
                            idx === selectedImageIndex ? 'border-primary-600' : 'border-gray-300'
                          }`}
                        >
                          <img
                            src={image}
                            alt={`Thumbnail ${idx + 1}`}
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              e.currentTarget.src = 'https://via.placeholder.com/64?text=?';
                            }}
                          />
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Lot Details */}
              <div className="mb-4 p-4 bg-gray-50 rounded-lg">
                <p className="text-sm text-gray-600 mb-1">Lot: {selectedLot.sku}</p>
                <p className="font-semibold mb-2">{selectedLot.title}</p>
                <p className="text-sm text-gray-600 mb-3">{selectedLot.description}</p>
                
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600">Starting Bid:</span>
                    <span className="font-semibold">${selectedLot.startingBid}</span>
                  </div>
                  {selectedLot.estimatedValue && (
                    <div className="flex items-center justify-between">
                      <span className="text-gray-600">Est. Value:</span>
                      <span className="font-semibold">{selectedLot.estimatedValue}</span>
                    </div>
                  )}
                </div>

                {/* Expert Estimate Range */}
                {selectedLot.expertLowEstimate && selectedLot.expertHighEstimate && (
                  <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded">
                    <p className="text-xs font-semibold text-blue-800 mb-2">🎯 Expert Estimate Range</p>
                    <div className="flex items-center justify-between">
                      <div className="text-center flex-1">
                        <p className="text-xs text-gray-600">Low</p>
                        <p className="text-lg font-bold text-blue-700">${selectedLot.expertLowEstimate}</p>
                      </div>
                      <div className="text-gray-400">—</div>
                      <div className="text-center flex-1">
                        <p className="text-xs text-gray-600">High</p>
                        <p className="text-lg font-bold text-blue-700">${selectedLot.expertHighEstimate}</p>
                      </div>
                    </div>
                    <p className="text-xs text-gray-600 mt-2 text-center">
                      Spread: ${selectedLot.expertHighEstimate - selectedLot.expertLowEstimate} 
                      ({(((selectedLot.expertHighEstimate - selectedLot.expertLowEstimate) / selectedLot.expertLowEstimate) * 100).toFixed(0)}%)
                    </p>
                  </div>
                )}
              </div>

              {/* Prediction & Wager Input */}
              <div className="space-y-4 mb-6">
                {/* Prediction Amount */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Your Predicted Final Price
                  </label>
                  <div className="flex items-center">
                    <span className="text-2xl mr-2">$</span>
                    <input
                      type="number"
                      value={predictionAmount}
                      onChange={(e) => setPredictionAmount(e.target.value)}
                      className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                      placeholder="0.00"
                      step="0.01"
                      min="0"
                    />
                  </div>
                  {predictionAmount && selectedLot.expertLowEstimate && selectedLot.expertHighEstimate && (
                    <div className="mt-2">
                      {parseFloat(predictionAmount) < selectedLot.expertLowEstimate || 
                       parseFloat(predictionAmount) > selectedLot.expertHighEstimate ? (
                        <p className="text-sm text-orange-600 font-semibold flex items-center">
                          <span className="mr-1">⚡</span>
                          Bold prediction! Outside expert range = higher rewards if correct!
                        </p>
                      ) : (
                        <p className="text-sm text-blue-600">
                          Within expert range (safer bet)
                        </p>
                      )}
                    </div>
                  )}
                </div>

                {/* Wager Amount */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Wager Amount (Ringer$)
                  </label>
                  <div className="space-y-2">
                    <input
                      type="number"
                      value={wagerAmount}
                      onChange={(e) => setWagerAmount(e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                      placeholder="Enter wager amount"
                      min="10"
                      step="10"
                    />
                    
                    {/* Quick Wager Buttons */}
                    <div className="flex gap-2">
                      {[50, 100, 250, 500].map(amount => (
                        <button
                          key={amount}
                          onClick={() => setWagerAmount(amount.toString())}
                          className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded hover:bg-gray-50"
                          disabled={balance < amount}
                        >
                          {amount} R$
                        </button>
                      ))}
                    </div>
                    
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-600">Your Balance:</span>
                      <span className="font-semibold">{balance} Ringer$</span>
                    </div>
                  </div>
                </div>

                {/* Execute as Bid Option */}
                <div className="border-t pt-4">
                  <div className="flex items-start space-x-3">
                    <input
                      type="checkbox"
                      id="executeAsBid"
                      checked={executeAsBid}
                      onChange={(e) => setExecuteAsBid(e.target.checked)}
                      className="mt-1 h-5 w-5 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                    />
                    <div className="flex-1">
                      <label htmlFor="executeAsBid" className="font-semibold text-gray-900 cursor-pointer">
                        💰 Execute as Left Bid (Absentee Bid)
                      </label>
                      <p className="text-sm text-gray-600 mt-1">
                        Allow the auctioneer to execute this as a real bid on your behalf during the live auction. 
                        You'll only pay if you win!
                      </p>
                    </div>
                  </div>

                  {executeAsBid && (
                    <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Your Maximum Bid ($)
                      </label>
                      <input
                        type="number"
                        value={maxBidAmount}
                        onChange={(e) => setMaxBidAmount(e.target.value)}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                        placeholder={`Minimum: $${selectedLot.startingBid}`}
                        min={selectedLot.startingBid}
                        step="0.01"
                      />
                      <p className="text-xs text-blue-700 mt-2">
                        💡 This is your <strong>maximum</strong> bid. The auctioneer will only bid as much as needed to win, up to this amount.
                      </p>
                      
                      <div className="mt-3 p-3 bg-white border border-blue-300 rounded">
                        <p className="text-xs font-semibold text-blue-800 mb-2">How Left Bids Work:</p>
                        <ul className="text-xs text-blue-700 space-y-1 ml-4 list-disc">
                          <li>Your max bid is kept confidential</li>
                          <li>Auctioneer bids on your behalf during live auction</li>
                          <li>You only pay the winning amount (not your max)</li>
                          <li>If outbid, you pay nothing</li>
                          <li>Your Ringer$ wager is still active for prediction scoring</li>
                        </ul>
                      </div>
                    </div>
                  )}
                </div>

                {/* Potential Return Calculator */}
                {wagerAmount && parseFloat(wagerAmount) > 0 && !executeAsBid && (
                  <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                    <p className="text-sm font-semibold text-green-800 mb-2">💰 Potential Returns</p>
                    <div className="space-y-1 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-700">Conservative (15% accurate):</span>
                        <span className="font-bold text-green-700">
                          {Math.round(parseFloat(wagerAmount) * 1.5)} R$ 
                          <span className="text-xs ml-1">(+{Math.round(parseFloat(wagerAmount) * 0.5)} profit)</span>
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-700">Great (5% accurate):</span>
                        <span className="font-bold text-green-700">
                          {Math.round(parseFloat(wagerAmount) * 5)} R$
                          <span className="text-xs ml-1">(+{Math.round(parseFloat(wagerAmount) * 4)} profit)</span>
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-700">Perfect (1% accurate, outside range):</span>
                        <span className="font-bold text-green-700">
                          {Math.round(parseFloat(wagerAmount) * 60)} R$
                          <span className="text-xs ml-1">(+{Math.round(parseFloat(wagerAmount) * 59)} profit)</span>
                        </span>
                      </div>
                    </div>
                    <p className="text-xs text-gray-600 mt-2">
                      ⚠️ Risk: If you're wrong or go over actual price, you lose your {wagerAmount} R$ wager
                    </p>
                  </div>
                )}
              </div>

              {/* Action Buttons */}
              <div className="flex space-x-3">
                <button
                  onClick={() => {
                    setSelectedLot(null);
                    setPredictionAmount('');
                    setWagerAmount('');
                    setMaxBidAmount('');
                    setExecuteAsBid(false);
                    setSelectedImageIndex(0);
                  }}
                  className="flex-1 btn-secondary"
                >
                  Cancel
                </button>
                <button
                  onClick={submitPrediction}
                  disabled={!predictionAmount || !wagerAmount || parseFloat(wagerAmount) > balance || (executeAsBid && !maxBidAmount)}
                  className="flex-1 btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {executeAsBid ? `Place Bid & Predict (${wagerAmount || 0} R$)` : `Place Bet (${wagerAmount || 0} R$)`}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AuctionDetail;